import urls from "global/constants/UrlConstants";
import {
  AuditChainVerification,
  DateRange,
  SortingConfig,
  RequestFile,
  SignerAttachment,
  SignRequestFieldData,
} from "models/interfaces";
import {
  getCallParams,
  getNoAuthCallParams,
  makeCall,
  makeCallFile,
  makeCallText,
} from "utils/service";
import { SignerIdvSummary } from "types/identityVerification";

export const fetchSentRequestCount = async (
  status: string,
  searchText: string,
  email: string,
  dateRange?: DateRange,
) => {
  let url = `${urls.SENT_REQUESTS}/count/status/${status}`;
  if (email) {
    url += `/email/${email}`;
  }
  const payload = { searchText: searchText, dateRange: dateRange };
  try {
    const callParams = await getCallParams("POST", payload);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchSentRequest = async (
  pageNumber: number,
  sortingData: SortingConfig[],
  status: string,
  searchText: string,
  email: string,
  dateRange?: DateRange,
) => {
  const url = urls.SENT_REQUESTS;
  try {
    const payload = {
      searchText: searchText,
      entitySorters: sortingData,
      dateRange: dateRange,
    };
    const callParams = await getCallParams("POST", payload);
    let apiURL: string = `${url}/status/${status ? status : "All"}`;
    if (email) {
      apiURL += `/email/${email}`;
    }
    apiURL += `/page/${pageNumber}`;

    const response = await makeCall(apiURL, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchInboxRequests = async (
  pageNumber: number,
  sortingData: SortingConfig[],
  status: string,
  searchText: string,
  dateRange?: DateRange,
) => {
  const url = urls.INBOX_REQUESTS;
  try {
    const payload = {
      searchText: searchText,
      entitySorters: sortingData,
      dateRange: dateRange,
    };
    const callParams = await getCallParams("POST", payload);
    let apiURL: string = `${url}/status/${status}`;
    apiURL += `/page/${pageNumber}`;

    const response = await makeCall(apiURL, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchInboxRequestsCount = async (
  status: string,
  searchText: string,
  dateRange?: DateRange,
) => {
  const url = urls.INBOX_REQUESTS;
  const payload = { searchText: searchText, dateRange: dateRange };
  try {
    const callParams = await getCallParams("POST", payload);
    let apiURL: string = `${url}/count/status/${status ? status : "All"}`;

    const response = await makeCall(apiURL, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const getAutoEmailReminder = async () => {
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(
      urls.GET_AUTO_EMAIL_REMINDER_CONFIGURATION,
      callParams,
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getDefaultDateFormat = async () => {
  try {
    const callParams = await getCallParams("GET");
    // The backend returns the raw format token (e.g. MM/dd/yyyy) as plain
    // text, not a JSON-encoded string — makeCall's JSON.parse would throw.
    const response = await makeCallText(
      urls.GET_DEFAULT_DATE_FORMAT_CONFIGURATION,
      callParams,
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const updateDefaultDateFormatConfig = async (
  javaDateFormat: string,
) => {
  const payload = { value: javaDateFormat };
  try {
    const callParams = await getCallParams("POST", payload);
    const response = await makeCall(
      urls.UPDATE_DEFAULT_DATE_FORMAT_CONFIGURATION,
      callParams,
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getAutoReminderConfig = async () => {
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(
      urls.GET_AUTO_REMINDER_CONFIGURATION,
      callParams,
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getAutoDelegateConfig = async (email: string) => {
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(
      urls.GET_AUTO_DELEGATE_CONFIGURATION + `/${email}/auto-delegation`,
      callParams,
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getExpirationDays = async () => {
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(
      urls.GET_EXPIRATION_DAYS_CONFIGURATION,
      callParams,
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const updateAutoEmailReminder = async (autoReminder: boolean) => {
  const payload = {
    value: autoReminder,
  };
  try {
    const callParams = await getCallParams("POST", payload);
    const response = await makeCall(
      urls.UPDATE_AUTO_EMAIL_REMINDER_CONFIGURATION,
      callParams,
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const updateAutoReminderConfig = async (
  daysBeforeFirstReminder: number,
  daysBetweenReminders: number,
) => {
  const payload = {
    value: JSON.stringify({
      daysBeforeFirstReminder: daysBeforeFirstReminder,
      daysBetweenReminders: daysBetweenReminders,
    }),
  };
  try {
    const callParams = await getCallParams("POST", payload);
    const response = await makeCall(
      urls.UPDATE_AUTO_REMINDER_CONFIGURATION,
      callParams,
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const updateExpirationDays = async (expirationDays: number) => {
  const payload = {
    value: expirationDays,
  };
  try {
    const callParams = await getCallParams("POST", payload);
    const response = await makeCall(
      urls.UPDATE_EXPIRATION_DAYS_CONFIGURATION,
      callParams,
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const updateDelegation = async (payload: any, email: string) => {
  try {
    const callParams = await getCallParams("PUT", payload);
    const response = await makeCall(
      `${urls.UPDATE_AUTO_DELEGATION_CONFIGURATION}/${email}/auto-delegation`,
      callParams,
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getSignRequestData = async (id: string) => {
  try {
    const url = `${urls.GET_VIEW_SIGN_REQUEST}/${id}`;
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Each non-CC signer's identity verification status on a request. A separate
 * call from the request detail; only made when the envelope requires IDV.
 */
export const getRequestIdentityVerification = async (
  requestId: string,
): Promise<SignerIdvSummary[]> => {
  try {
    const url = `${urls.GET_VIEW_SIGN_REQUEST}/${requestId}/${urls.IDV_REQUEST_SUFFIX}`;
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return Array.isArray(response) ? response : [];
  } catch (error) {
    throw error;
  }
};

// Mints the signed callback key for the logged-in user's own requestToken (see handleSignNow in
// MySignRequestHelper) so the in-app "Sign Now" launch carries a valid ?id=&key= pair instead of the
// empty key that 401s at the backend's CryptoService.validateClientSecret gate.
export const fetchCallbackKeyForSignNow = async (requestToken: string) => {
  try {
    const url = `${urls.GET_CALLBACK_KEY_FOR_SIGN_NOW}/${requestToken}`;
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response.message as string;
  } catch (error) {
    throw error;
  }
};

// Recipient-side counterpart of getSignRequestData: the id-based fetch is scoped to the sender's
// account and comes back empty for a request the user only received, so the Inbox loads it through
// the signer callback route with a key minted for the user's own requestToken.
export const getSignRequestDataByToken = async (requestToken: string) => {
  try {
    const key = await fetchCallbackKeyForSignNow(requestToken);
    const url = `${urls.FETCH_SIGN_REQ}/${requestToken}?id=${encodeURIComponent(requestToken)}&key=${encodeURIComponent(key)}`;
    const callParams = await getNoAuthCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error) {
    throw error;
  }
};

export const getRequestAudit = async (reqId: string) => {
  try {
    const url = `${urls.FETCH_AUDITS}/${reqId}`;
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Re-checks the request's audit trail against its hash chain. Called on demand
 * only: a BROKEN result raises a backend alert, so it must not run per page view.
 */
export const verifyRequestAudit = async (
  reqId: string,
): Promise<AuditChainVerification> => {
  try {
    const url = `${urls.VERIFY_AUDIT}/${reqId}`;
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response as AuditChainVerification;
  } catch (error) {
    throw error;
  }
};

export const getDeclinedSignerDocumentPresignedUrl = async (
  envelopeId: string,
  documentFile: RequestFile,
) => {
  try {
    const url = `${urls.FETCH_HIGHLIGHTED_DOCUMENTS}/${envelopeId}`;
    const callParams = await getCallParams("POST", documentFile);
    const presignedUrl = await makeCall(url, callParams);
    return presignedUrl;
  } catch (error) {
    console.error("Error in getDeclinedSignerDocumentPresignedUrl:", error);
    throw error;
  }
};

export const downloadSignedPdfAsZip = async (file: any) => {
  const url = `${urls.DOWNLOAD_DOCS_AS_ZIP}`;
  try {
    const callParams = await getCallParams("POST", file);
    const response = await makeCallFile(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchRequestAttachments = async (
  requestId: string,
): Promise<SignerAttachment[]> => {
  const url = `${urls.FETCH_REQUEST_ATTACHMENTS}/${requestId}/limit/ALL/page/1`;
  const callParams = await getCallParams("GET");
  const response = await makeCall(url, callParams);
  return Array.isArray(response) ? response : [];
};

export const downloadRequestAttachment = async (
  attachmentId: string,
): Promise<Blob> => {
  const url = `${urls.DOWNLOAD_REQUEST_ATTACHMENT}/${attachmentId}/download`;
  const callParams = await getCallParams("GET");
  return await makeCallFile(url, callParams);
};

export const downloadAllRequestAttachments = async (
  requestId: string,
): Promise<Blob> => {
  const url = `${urls.DOWNLOAD_ALL_REQUEST_ATTACHMENTS}/${requestId}`;
  const callParams = await getCallParams("GET");
  return await makeCallFile(url, callParams);
};

export const fetchRequestFieldData = async (
  requestId: string,
): Promise<SignRequestFieldData[]> => {
  const url = `${urls.FETCH_REQUEST_FIELD_DATA}/${requestId}`;
  const callParams = await getCallParams("GET");
  const response = await makeCall(url, callParams);
  return Array.isArray(response) ? response : [];
};

export const downloadRequestFieldDataCsv = async (
  requestId: string,
): Promise<Blob> => {
  const url = `${urls.DOWNLOAD_REQUEST_FIELD_DATA}/${requestId}`;
  const callParams = await getCallParams("GET");
  return await makeCallFile(url, callParams);
};

export const voidSignRequest = async (reqId: string) => {
  try {
    const url = `${urls.VOID_SIGN_REQ}/${reqId}`;
    const callParams = await getCallParams("POST", null);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error) {
    throw error;
  }
};

export const resendSignRequest = async (reqId: string, email?: string) => {
  try {
    const url = email
      ? `${urls.RESEND_SIGN_REQ}/${reqId}/${email}`
      : `${urls.RESEND_SIGN_REQ}/${reqId}`;
    const callParams = await getCallParams("POST", null);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error) {
    throw error;
  }
};

export const getPdfSignRequestCount = async (
  status: string = "All",
  searchText: string,
  email: string = "",
  dateRange?: DateRange,
) => {
  let url = `${urls.SIGN_REQ_LIST_COUNT}/status/${status}`;
  if (email) {
    url += `/email/${email}`;
  }
  const payload = { searchText: searchText, dateRange: dateRange };
  try {
    const callParams = await getCallParams("POST", payload);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const getUnsignedDocumentUrl = async (
  pdfFile: any,
  isRequest: boolean,
  envelopeId: string,
  abortSignal?: AbortSignal | undefined,
) => {
  const url = isRequest ? `${urls.UNSIGEND_DOC_URL}/${isRequest}/envelopeId/${envelopeId}` : `${urls.TEMPLATE_UNSIGEND_DOC_URL}/${isRequest}/envelopeId/${envelopeId}`;
  try {
    const callParams: any = await getCallParams("POST", pdfFile);
    callParams.signal = abortSignal;
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};
